import type { Partner as ApiPartner } from '@/api/partners';
import { ExpiredIdCardOverride, PartnerSearchSelect } from '@/components/partners';
import { Card, CardContent } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { useCallback } from 'react';
import type { Partner } from '../types';
import { useWizardStore } from '../wizard-store';
import { RentalPartnerExtra } from './RentalPartnerExtra';

/** Map API partner to local wizard Partner type */
function toWizardPartner(p: ApiPartner): Partner {
  return {
    id: p.id,
    name: p.name,
    email: p.email,
    phone: p.phone,
    type: p.type === 'company' ? 'COMPANY' : 'INDIVIDUAL',
    status: (p.status as Partner['status']) ?? 'active',
    taxNumber: p.taxNumber,
    address: p.address,
    city: p.city,
    isVip: p.loyaltyPoints > 1000,
    loyaltyPoints: p.loyaltyPoints,
    creditLimit: p.creditLimit ?? 0,
    balance: p.currentBalance,
    warningNote: p.warningNote,
    costBearerId: p.costBearerId,
    costBearerName: p.costBearerName,
    maskedIdCardNumber: p.maskedIdCardNumber,
    // Story 70-1E-a: forward idCardExpiry so PartnerStep can warn on past date
    idCardExpiry: p.idCardExpiry,
  };
}

export function PartnerStep() {
  const { t } = useTranslation();
  const { partner: selectedPartner, setPartner, setExpiredIdOverride } = useWizardStore();

  const handleSelect = (apiPartner: ApiPartner) => {
    setPartner(toWizardPartner(apiPartner));
  };

  const handleOverrideChange = useCallback(
    (state: { override: boolean; reason: string }) => {
      setExpiredIdOverride(state.override, state.reason);
    },
    [setExpiredIdOverride]
  );

  // S1-2: inicálé a kiválasztott-partner kártya avatarjához (max 2 szókezdő).
  const initials = (selectedPartner?.name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();
  const renterLocation = [selectedPartner?.city, selectedPartner?.address]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="lg:grid lg:grid-cols-2 lg:items-start lg:gap-6">
      {/* Bal oszlop: cím + keresés + visszaigazoló-doboz + lejárt-ig. override.
          Mobilon ez az EGYETLEN oszlop (a jobb panel lg:-only) → 0 mobil-változás. */}
      <div className="space-y-6">
        <div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('rental.wizard.selectPartner')}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('rental.wizard.selectPartnerDesc')}
          </p>
        </div>

        {/* FE-A4 klón-CTA SUPERSEDED (2026-06-07): a „Mint múltkor" RentalLastRentalBanner
            (equipment-step teteje) az egyetlen clone-belépési pont — a közös
            useLastRentalClone hookkal. A korábbi PartnerStep-gomb eltávolítva. */}

        <PartnerSearchSelect
          onSelect={handleSelect}
          showRecent
          placeholder={t('rental.wizard.searchPartnerPlaceholder')}
          value={selectedPartner ? ({ id: selectedPartner.id } as ApiPartner) : null}
          renderExtra={partner => <RentalPartnerExtra partner={partner} />}
        />

        {selectedPartner && (
          <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-4">
            <div className="flex items-center gap-2">
              <span className="inline-flex shrink-0 items-center rounded-full bg-green-600 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white">
                {t('rental.wizard.selectedBadge')}
              </span>
              <p className="text-sm font-medium text-green-800 dark:text-green-300">
                {t('rental.wizard.selectedPartner', { name: selectedPartner.name })}
              </p>
            </div>
          </div>
        )}

        {/* Story 70-1E-a: expired ID card override (renders only if expiry in past). */}
        {selectedPartner && (
          <ExpiredIdCardOverride
            idCardExpiry={selectedPartner.idCardExpiry}
            onChange={handleOverrideChange}
            data-testid="rental-partner-expired-override"
          />
        )}
      </div>

      {/* S1-2: dedikált kiválasztott-partner kártya a jobb panelben (CSAK desktopon;
          mobilon a bal-oszlop visszaigazoló-doboza + badge viszi). */}
      <div className="mt-6 hidden lg:mt-0 lg:block">
        {selectedPartner && (
          <Card data-testid="rental-partner-selected-card">
            <CardContent className="p-5">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-base font-bold text-primary-foreground">
                    {initials}
                  </span>
                  <div className="min-w-0">
                    <div className="truncate text-base font-bold text-gray-900 dark:text-white">
                      {selectedPartner.name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {selectedPartner.type === 'COMPANY'
                        ? t('partner.company')
                        : t('partner.individual')}
                    </div>
                  </div>
                </div>
                <span className="inline-flex shrink-0 items-center rounded-full bg-green-600 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white">
                  {t('rental.wizard.selectedBadge')}
                </span>
              </div>
              <dl className="space-y-1 text-xs">
                {selectedPartner.taxNumber && (
                  <div className="flex justify-between gap-2">
                    <dt className="text-muted-foreground">{t('partner.taxNumber')}</dt>
                    <dd className="text-gray-900 dark:text-white">{selectedPartner.taxNumber}</dd>
                  </div>
                )}
                {selectedPartner.phone && (
                  <div className="flex justify-between gap-2">
                    <dt className="text-muted-foreground">{t('partner.phone')}</dt>
                    <dd className="text-gray-900 dark:text-white">{selectedPartner.phone}</dd>
                  </div>
                )}
                {renterLocation && (
                  <div className="flex justify-between gap-2">
                    <dt className="text-muted-foreground">{t('partner.address')}</dt>
                    <dd className="truncate text-gray-900 dark:text-white">{renterLocation}</dd>
                  </div>
                )}
                {selectedPartner.maskedIdCardNumber && (
                  <div className="flex justify-between gap-2">
                    <dt className="text-muted-foreground">{t('partner.idCardNumber')}</dt>
                    <dd className="text-gray-900 dark:text-white">
                      {selectedPartner.maskedIdCardNumber}
                    </dd>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
